import { c as _c } from "react/compiler-runtime";
import type { Color } from '@onefootprint/design-tokens';
import { IcoGlobe16 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { createElement, useState } from 'react';
import { type DeviceInsightEvent, type DeviceVisitEvent, isDeviceVisit } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import { icoForUserAgent } from '@/utils/user-agent';
import type { MapMarkerProps } from '../../map/components/map-marker';
import type { MapLine } from '../../map/map';
import AddressType from '../components/address-card/types';
import getIconForAddress from '../utils/get-icon-for-address';
import useAddressCoordinates from './use-address-coordinates';
import useCountryCoordinates from './use-country-coordinates';
import type { MultiSelectOption, MultiSelectOptionValue } from './use-multi-select-options';
type Coordinates = {
  lat: number;
  lng: number;
};
type Entry = {
  id: string; // unique id
  type: MultiSelectOptionValue;
  coordinates?: Coordinates;
  data?: DeviceInsightEvent | DeviceVisitEvent;
  /** The card of the entry a pin belongs to, for a pin without a card of its own */
  cardId?: string;
  cardisPending?: boolean;
  marker?: MapMarkerProps;
};
const isDeviceInAnotherCountry = ({
  event: {
    data
  }
}: DeviceVisitEvent) => !!data.deviceCountry && data.deviceCountry !== data.ipCountry;

/** A pin at the IP's location and, for a device in another country, a pin on that country joined to it */
const getVisitEntries = (visit: DeviceVisitEvent, countryCoordinates: Record<string, Coordinates>) => {
  const {
    id
  } = visit;
  const {
    ipLatitude: lat,
    ipLongitude: lng,
    deviceCountry,
    userAgent
  } = visit.event.data;
  const ipCoords = typeof lat === 'number' && typeof lng === 'number' ? {
    lat,
    lng
  } : undefined;
  const entries: Entry[] = [{
    id,
    type: 'device_visit',
    coordinates: ipCoords,
    data: visit,
    marker: ipCoords ? {
      id,
      ...ipCoords,
      getIcon: (color?: Color) => icoForUserAgent(userAgent ?? '', color ?? 'quinary', 'small')
    } : undefined
  }];
  const deviceCoords = deviceCountry && isDeviceInAnotherCountry(visit) ? countryCoordinates[deviceCountry] : undefined;
  if (!deviceCoords) {
    return {
      entries
    };
  }
  const deviceId = `${id}-device`;
  entries.push({
    id: deviceId,
    type: 'device_visit',
    coordinates: deviceCoords,
    cardId: id,
    marker: {
      id: deviceId,
      ...deviceCoords,
      getIcon: (color?: Color) => createElement(IcoGlobe16, {
        color: color ?? 'quinary'
      })
    }
  });
  const line: MapLine | undefined = ipCoords ? {
    id,
    from: ipCoords,
    to: deviceCoords
  } : undefined;
  return {
    entries,
    line
  };
};
const useEntries = (entity, deviceInsights, options) => {
  const $ = _c(54);
  const {
    data: businessData,
    isPending: businessCoordLoading,
    isError: businessError
  } = useAddressCoordinates(entity, AddressType.business);
  const {
    data: residentialData,
    isPending: residentialCoordLoading,
    isError: residentialError
  } = useAddressCoordinates(entity, AddressType.residential);
  let t0;
  let t1;
  if ($[0] !== deviceInsights) {
    const visits = deviceInsights.filter(isDeviceVisit);
    t1 = useCountryCoordinates;
    t0 = new Set(visits.filter(isDeviceInAnotherCountry).flatMap(_temp));
    $[0] = deviceInsights;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = [...t0];
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const countryCoordinates = t1(t2);
  let innerEntries;
  let lines;
  if ($[5] !== businessCoordLoading || $[6] !== businessData || $[7] !== businessError || $[8] !== countryCoordinates || $[9] !== deviceInsights || $[10] !== options || $[11] !== residentialCoordLoading || $[12] !== residentialData || $[13] !== residentialError) {
    lines = [];
    let t3;
    if ($[16] !== options) {
      t3 = new Set(options.map(_temp2));
      $[16] = options;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    const selectedOptionsSet = t3;
    innerEntries = {};
    deviceInsights.forEach(event_0 => {
      if (isDeviceVisit(event_0)) {
        if (selectedOptionsSet.has("device_visit")) {
          const visitEntries = getVisitEntries(event_0, countryCoordinates);
          visitEntries.entries.forEach(entry => {
            innerEntries[entry.id] = entry;
          });
          if (visitEntries.line) {
            lines.push(visitEntries.line);
          }
        }
        return;
      }
      if (!selectedOptionsSet.has(event_0.event.data.kind)) {
        return;
      }
      const {
        data: t4
      } = event_0.event;
      const {
        insight,
        kind
      } = t4;
      const lat = insight.latitude;
      const lng = insight.longitude;
      const hasCoords = !!lat && !!lng;
      const id = `${insight.timestamp}-${lat}-${lng}-${kind}`;
      innerEntries[id] = {
        id,
        type: kind,
        coordinates: hasCoords ? {
          lat,
          lng
        } : undefined,
        data: event_0,
        marker: hasCoords ? {
          id,
          lat,
          lng,
          getIcon: color => icoForUserAgent(insight?.userAgent ?? "", color ?? "quinary", "small")
        } : undefined
      };
    });
    const isBusinessAddressSelected = selectedOptionsSet.has("businessAddress");
    if (isBusinessAddressSelected) {
      let t5;
      if ($[18] !== businessData) {
        t5 = businessData || {};
        $[18] = businessData;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      const {
        lat: businessLat,
        lng: businessLng
      } = t5;
      const hasCoords_0 = businessLat !== undefined && businessLng !== undefined;
      let t6;
      if ($[20] !== businessLat || $[21] !== businessLng || $[22] !== hasCoords_0) {
        t6 = hasCoords_0 ? {
          lat: businessLat,
          lng: businessLng
        } : undefined;
        $[20] = businessLat;
        $[21] = businessLng;
        $[22] = hasCoords_0;
        $[23] = t6;
      } else {
        t6 = $[23];
      }
      const t7 = businessCoordLoading && !businessError;
      let t8;
      if ($[24] !== businessLat || $[25] !== businessLng || $[26] !== hasCoords_0) {
        t8 = hasCoords_0 ? {
          id: "business",
          lat: businessLat,
          lng: businessLng,
          getIcon: _temp3
        } : undefined;
        $[24] = businessLat;
        $[25] = businessLng;
        $[26] = hasCoords_0;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      let t9;
      if ($[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
        t9 = {
          id: "business",
          type: "businessAddress",
          coordinates: t6,
          cardisPending: t7,
          marker: t8
        };
        $[28] = t6;
        $[29] = t7;
        $[30] = t8;
        $[31] = t9;
      } else {
        t9 = $[31];
      }
      innerEntries.business = t9;
    }
    const isResidentialAddressSelected = selectedOptionsSet.has("residentialAddress");
    if (isResidentialAddressSelected) {
      let t5;
      if ($[32] !== residentialData) {
        t5 = residentialData || {};
        $[32] = residentialData;
        $[33] = t5;
      } else {
        t5 = $[33];
      }
      const {
        lat: residentialLat,
        lng: residentialLng
      } = t5;
      const hasCoords_1 = residentialLat !== undefined && residentialLng !== undefined;
      let t6;
      if ($[34] !== hasCoords_1 || $[35] !== residentialLat || $[36] !== residentialLng) {
        t6 = hasCoords_1 ? {
          lat: residentialLat,
          lng: residentialLng
        } : undefined;
        $[34] = hasCoords_1;
        $[35] = residentialLat;
        $[36] = residentialLng;
        $[37] = t6;
      } else {
        t6 = $[37];
      }
      const t7 = residentialCoordLoading && !residentialError;
      let t8;
      if ($[38] !== hasCoords_1 || $[39] !== residentialLat || $[40] !== residentialLng) {
        t8 = hasCoords_1 ? {
          id: "residential",
          lat: residentialLat,
          lng: residentialLng,
          getIcon: _temp4
        } : undefined;
        $[38] = hasCoords_1;
        $[39] = residentialLat;
        $[40] = residentialLng;
        $[41] = t8;
      } else {
        t8 = $[41];
      }
      let t9;
      if ($[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
        t9 = {
          id: "residential",
          type: "residentialAddress",
          coordinates: t6,
          cardisPending: t7,
          marker: t8
        };
        $[42] = t6;
        $[43] = t7;
        $[44] = t8;
        $[45] = t9;
      } else {
        t9 = $[45];
      }
      innerEntries.residential = t9;
    }
    $[5] = businessCoordLoading;
    $[6] = businessData;
    $[7] = businessError;
    $[8] = countryCoordinates;
    $[9] = deviceInsights;
    $[10] = options;
    $[11] = residentialCoordLoading;
    $[12] = residentialData;
    $[13] = residentialError;
    $[14] = innerEntries;
    $[15] = lines;
  } else {
    innerEntries = $[14];
    lines = $[15];
  }
  const entries = innerEntries;
  const [selectedId, setSelectedId] = useState(null);
  const selectedCoords = selectedId ? entries[selectedId]?.coordinates : undefined;
  let t3;
  if ($[46] !== selectedId) {
    t3 = id_2 => {
      if (id_2 === selectedId) {
        setSelectedId(null);
      } else {
        setSelectedId(id_2);
      }
    };
    $[46] = selectedId;
    $[47] = t3;
  } else {
    t3 = $[47];
  }
  const onSelectedIdChange = t3;
  let t4;
  if ($[48] !== entries || $[49] !== lines || $[50] !== onSelectedIdChange || $[51] !== selectedCoords || $[52] !== selectedId) {
    t4 = {
      entries,
      lines,
      selectedCoords,
      selectedId,
      onSelectedIdChange
    };
    $[48] = entries;
    $[49] = lines;
    $[50] = onSelectedIdChange;
    $[51] = selectedCoords;
    $[52] = selectedId;
    $[53] = t4;
  } else {
    t4 = $[53];
  }
  return t4;
};
export default useEntries;
function _temp(t0) {
  const {
    event
  } = t0;
  return event.data.deviceCountry ?? [];
}
function _temp2(option) {
  return option.value;
}
function _temp3(color_0) {
  return getIconForAddress(AddressType.business, color_0, "small");
}
function _temp4(color_1) {
  return getIconForAddress(AddressType.residential, color_1, "small");
}